// src/components/sessions/modals/ManageStudentsModal.jsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { XCircleIcon } from '@heroicons/react/24/outline';

const ManageStudentsModal = ({
  isOpen,
  onClose,
  enrollStudentError,
  studentSearchQuery,
  setStudentSearchQuery,
  studentTabView,
  setStudentTabView,
  filteredCourseStudents,
  filteredAvailableStudents,
  handleEnrollStudent,
  handleUnenrollStudent
}) => {
  const { t } = useTranslation();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[80vh] overflow-hidden">
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-slate-800">
              {t('sessions.modals.manageStudents.title')}
            </h3>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600"
              aria-label={t('common.close')}
            >
              <XCircleIcon className="w-6 h-6" />
            </button>
          </div>
          <p className="text-xs text-amber-700 mt-2">
            {t('sessions.modals.manageStudents.adminHint')}
          </p>
        </div>

        <div className="p-6">
          {enrollStudentError && (
            <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md text-sm">
              {enrollStudentError}
            </div>
          )}

          <div className="mb-4">
            <input
              type="text"
              placeholder={t('sessions.modals.manageStudents.searchPlaceholder')}
              value={studentSearchQuery}
              onChange={(e) => setStudentSearchQuery(e.target.value)}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
            />
          </div>

          <div className="mb-4">
            <div className="flex space-x-1 border-b border-slate-200">
              <button
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  studentTabView === 'current'
                    ? 'text-cyan-600 border-b-2 border-cyan-600'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
                onClick={() => setStudentTabView('current')}
              >
                {t('sessions.modals.manageStudents.tabs.current', { count: filteredCourseStudents.length })}
              </button>
              <button
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  studentTabView === 'available'
                    ? 'text-cyan-600 border-b-2 border-cyan-600'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
                onClick={() => setStudentTabView('available')}
              >
                {t('sessions.modals.manageStudents.tabs.available', { count: filteredAvailableStudents.length })}
              </button>
            </div>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {studentTabView === 'current' ? (
              filteredCourseStudents.length === 0 ? (
                <p className="text-center text-slate-500 py-8">
                  {studentSearchQuery 
                    ? t('sessions.modals.manageStudents.noSearchResults') 
                    : t('sessions.modals.manageStudents.noCurrentStudents')}
                </p>
              ) : (
                <div className="space-y-2">
                  {filteredCourseStudents.map(student => (
                    <div key={student.institution_member_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div>
                        <p className="font-medium text-slate-800">
                          {student.display_name || student.full_name || student.email}
                        </p>
                        <p className="text-sm text-slate-600">{student.email}</p>
                      </div>
                      <button
                        onClick={() => handleUnenrollStudent(student.institution_member_id)}
                        className="px-3 py-1 text-sm text-red-600 hover:bg-red-50 rounded-md transition-colors"
                      >
                        {t('sessions.modals.manageStudents.remove')}
                      </button>
                    </div>
                  ))}
                </div>
              )
            ) : (
              filteredAvailableStudents.length === 0 ? (
                <p className="text-center text-slate-500 py-8">
                  {studentSearchQuery 
                    ? t('sessions.modals.manageStudents.noSearchResults') 
                    : t('sessions.modals.manageStudents.noAvailableStudents')}
                </p>
              ) : (
                <div className="space-y-2">
                  {filteredAvailableStudents.map(student => (
                    <div key={student.institution_member_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div>
                        <p className="font-medium text-slate-800">
                          {student.display_name || student.full_name || student.email}
                        </p>
                        <p className="text-sm text-slate-600">{student.email}</p>
                      </div>
                      <button
                        onClick={() => handleEnrollStudent(student.institution_member_id)}
                        className="px-3 py-1 text-sm text-cyan-600 hover:bg-cyan-50 rounded-md transition-colors"
                      >
                        {t('sessions.modals.manageStudents.add')}
                      </button>
                    </div>
                  ))}
                </div>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageStudentsModal;